// Single source of truth for the marketing home's same-origin product shots.
//
// Most product screenshots on the site are served from the media bucket and
// resolved at runtime through the manifest (see MediaImage). This one is not:
// its image target carries `deliverySubdomain: 'www'`, so the render writes it
// into the committed www tree at build-stable paths. That is what lets the markup
// and the pre-paint preload in index.html name it without the manifest — which
// only the JS bundle can read, far too late for a preload.
//
// Home.tsx and marketing/scripts/targets.ts both import these, so the render emits
// exactly the widths the `<img srcset>` asks for; a width the render never produced
// would 404.

// The document mid-edit, in the home page's about box. The first image on the
// page and the likely LCP element on a tall viewport, which is the whole reason it
// is served from here rather than the bucket.
export const FLOW_SHOT_WIDTHS = [640, 960, 1280] as const;

// Display width by viewport: the width of the home page's text column, which
// the media between the boxes matches — the container less its padding, at the
// container's cap and below it. Mirrored by the preload's `imagesizes` in
// index.html — keep the two in agreement or the browser preloads one candidate
// and then picks another.
export const FLOW_SHOT_SIZES = '(min-width: 1080px) 1032px, calc(100vw - 48px)';

// `srcset` for a theme-matched shot: dark under graphite, light otherwise. The
// filenames are `<source>-<scheme>[-<width>].webp`, which is what render writes
// for a `www` delivery target.
export function shotSrcset(
  source: string,
  scheme: 'dark' | 'light',
  widths: readonly number[],
): string {
  return widths.map((w) => `/marketing/${source}-${scheme}-${w}.webp ${w}w`).join(', ');
}

// The hand-recorded agent demo: an agent in Claude Desktop creating a document
// and drafting its contents over MCP, with the Goalpost app beside it. A `copy`
// target of the same name (marketing/scripts/targets.ts): the recording lives in
// the media bucket's library/ as `agent-desktop-demo-light-cc.mp4` with its
// `-poster.webp`, and render + `marketing:asset:push` publish it, after which the
// pages that embed it light up. Light only (it records a light-themed desktop),
// so MediaVideo's theme fallback shows the same cut under the dark theme.
export const AGENT_DEMO_VIDEO = 'agent-desktop-demo';

// The hand-recorded MCP app demo for the blog announcement: Claude opening a
// document beside the conversation and both sides editing it. Published the same
// way as AGENT_DEMO_VIDEO, by the `mcp-app-demo` copy target. Light only, with
// MediaVideo's theme fallback covering the dark theme.
export const MCP_APP_DEMO_VIDEO = 'mcp-app-demo';
